import { FormQuestionSection, MultipleSectionAdd, MultipleSectionAddAnother } from '@form';
import React, { useEffect, useState } from 'react';
import { getNextSection, getRequestedDataIndex } from '@util/multiple-sections';
import { useLocation, useParams } from 'react-router-dom';
import { toggleMandatory } from '@services/form-data-section';

import { PageNotFound } from '@pages';
import { createFutureRequiredQuestionsFromQuestionsAndProps } from '@util/warning-summary';

const FormSection = props => {
    const params = useParams();
    const location = useLocation();

    const [state, setState] = useState({
        isFirstSection: undefined,
        multipleSectionCount: undefined,
        nextPath: undefined,
        questions: undefined,
        render: undefined,
        sectionSpec: undefined,
        showSaveAndExitButton: undefined,
        showSaveAndSkipButton: undefined,
        showSaveAndGoToSummaryButton: undefined,
        futureRequiredQuestions: undefined
    });

    const getQuestionIds = questionRefs =>
        (Array.isArray(questionRefs) ? questionRefs : questionRefs ? [questionRefs] : [])
            .map(question => (typeof question === 'string' ? question : question?.id))
            .filter(Boolean);

    const applyConditionalRevealsFromSavedAnswers = questions => {
        let updatedQuestions = questions.map(question => ({ ...question }));

        updatedQuestions.forEach(question => {
            if (!question.options || !['radio', 'select', 'checkbox'].includes(question.type)) {
                return;
            }

            const getAllChildIds = option => {
                return getQuestionIds([
                    ...(option?.questions || []),
                    ...(option?.linked_question_id
                        ? Array.isArray(option.linked_question_id)
                            ? option.linked_question_id
                            : [option.linked_question_id]
                        : []),
                    ...(option?.hide_linked_question === false
                        ? question.linked_questions || []
                        : [])
                ]);
            };

            const getSelectedChildIds = option => {
                if (option?.hide_linked_question === false && !option?.linked_question_id) {
                    return question.linked_questions
                        ? getQuestionIds(question.linked_questions)
                        : [];
                }

                return getQuestionIds([
                    ...(option?.questions || []),
                    ...(option?.linked_question_id
                        ? Array.isArray(option.linked_question_id)
                            ? option.linked_question_id
                            : [option.linked_question_id]
                        : [])
                ]);
            };

            const allChildIds = question.options.flatMap(getAllChildIds);

            const selectedValues =
                question.value === undefined || question.value === null || question.value === ''
                    ? []
                    : Array.isArray(question.value)
                    ? question.value
                    : [question.value];

            const selectedOptions = question.options.filter(option =>
                selectedValues.includes(option.key)
            );

            let selectedChildIds = selectedOptions.flatMap(getSelectedChildIds);

            if (question.id.includes('staff-group')) {
                const linkedQuestionIds = selectedOptions
                    .flatMap(option => option.questions || [])
                    .flatMap(childQuestion => childQuestion.linked_questions || [])
                    .map(linkedQuestion =>
                        typeof linkedQuestion === 'string' ? linkedQuestion : linkedQuestion?.id
                    )
                    .filter(Boolean);

                selectedChildIds = selectedOptions
                    .flatMap(option => option.questions || [])
                    .filter(childQuestion => childQuestion.hidden !== true)
                    .filter(childQuestion => !linkedQuestionIds.includes(childQuestion.id))
                    .map(childQuestion => childQuestion.id);
            }

            updatedQuestions = updatedQuestions.map(childQuestion => {
                if (selectedChildIds.includes(childQuestion.id)) {
                    return {
                        ...childQuestion,
                        hidden: false,
                        includeInApiPayload: true,
                        showOnSummaryPage: true
                    };
                }

                if (allChildIds.includes(childQuestion.id)) {
                    return {
                        ...childQuestion,
                        hidden: true,
                        showOnFormSection: false,
                        includeInApiPayload: false,
                        showOnSummaryPage: false
                    };
                }

                return childQuestion;
            });
        });

        return updatedQuestions;
    };

    useEffect(() => {
        const section = props.data.find(s => s.id === params.section);
        const allQuestions = props.data.flatMap(s => s.questions);
        if (section) {
            for (const question of section.questions) {
                if (question.toggle_mandatory !== undefined) {
                    const referenceQuestion = allQuestions.find(
                        q => q.id === question.toggle_mandatory
                    );
                    const option = referenceQuestion?.options.find(
                        o => o.key === referenceQuestion.value
                    );
                    if (option.set_mandatory !== undefined) {
                        toggleMandatory(option.set_mandatory, props.data, true);
                    } else if (option.set_optional !== undefined) {
                        toggleMandatory(option.set_optional, props.data, false);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.section]);

    useEffect(() => {
        const update = props.collection.form ? true : false;

        if (update) {
            const sectionSpecs = props.collection.form.sections;
            const sectionId = params.section;
            const collectionUrlSlug = props.collection.url_slug;
            const sectionSpecIndex = sectionSpecs.findIndex(x => x.id === sectionId);
            const sectionSpec = sectionSpecs.find(x => x.id === sectionId);

            if (!sectionSpec) {
                setState(prevState => ({ ...prevState, render: 'not-found' }));
                return;
            }

            const { nextPath, nextSectionId } = getNextSection(
                sectionSpec,
                sectionSpecs,
                props.data,
                collectionUrlSlug
            );

            const indexParam = params.index;
            const index = indexParam ? Number(indexParam) : undefined;
            const dataIndex = getRequestedDataIndex(props.data, sectionId, index);
            const dataSection = props.data[dataIndex] || { questions: [] };
            const questions = applyConditionalRevealsFromSavedAnswers(dataSection.questions).map(
                question => ({
                    ...question,
                    ref: question.ref || React.createRef()
                })
            );

            const futureRequiredQuestions = createFutureRequiredQuestionsFromQuestionsAndProps(
                questions,
                props
            );

            let render;

            if (sectionSpec.allow_multiple && !index) {
                render = 'multiple-section-add';
            } else if (sectionSpec.allow_multiple && location.hash === '#add-another') {
                render = 'multiple-section-add-another';
            } else {
                render = 'form-questions';
            }

            setState({
                isFirstLoad: dataSection.isFirstLoad,
                isFirstSection: sectionSpecIndex === 0 ? true : false,
                multipleSectionCount: props.data.filter(x => x.id === sectionSpec.id).length,
                nextPath: nextPath,
                nextSectionId,
                questions: questions,
                render: render,
                sectionSpec: sectionSpec,
                showSaveAndExitButton: props.collection.show_save_and_exit_button,
                showSaveAndSkipButton: props.collection.show_save_and_skip_to_summary_button,
                showSaveAndGoToSummaryButton: props.collection.show_save_and_go_to_summary_button,
                futureRequiredQuestions
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.collection, props.data, params.section, params.index, location.hash]);

    switch (state.render) {
        case 'not-found':
            return <PageNotFound />;

        case 'form-questions':
            return (
                <FormQuestionSection
                    collection={props.collection}
                    isFirstLoad={state.isFirstLoad}
                    isFirstSection={state.isFirstSection}
                    multipleSectionCount={state.multipleSectionCount}
                    nextPath={state.nextPath}
                    nextSectionId={state.nextSectionId}
                    onConditionalReveal={props.onConditionalReveal}
                    onCopyAnswer={props.onCopyAnswer}
                    onQuestionChange={props.onQuestionChange}
                    onRemoveSection={props.onRemoveSection}
                    onResetForm={props.onResetForm}
                    onSectionSubmit={props.onSectionSubmit}
                    onSkipSection={props.onSkipSection}
                    onSkipSections={props.onSkipSections}
                    onSubmissionIdChange={props.onSubmissionIdChange}
                    orgCode={props.orgCode}
                    questions={state.questions}
                    formData={props.data}
                    sectionSpec={state.sectionSpec}
                    showSaveAndExitButton={state.showSaveAndExitButton}
                    showSaveAndGoToSummaryButton={state.showSaveAndGoToSummaryButton}
                    showSaveAndSkipButton={state.showSaveAndSkipButton}
                    handleDisable={props.handleDisable}
                    futureRequiredQuestions={state.futureRequiredQuestions}
                    submissionId={props.submissionId}
                />
            );

        case 'multiple-section-add':
            return (
                <MultipleSectionAdd
                    collection={props.collection}
                    nextPath={state.nextPath}
                    onAddMultipleSection={props.onAddMultipleSection}
                    sectionSpec={state.sectionSpec}
                />
            );

        case 'multiple-section-add-another':
            return (
                <MultipleSectionAddAnother
                    nextPath={state.nextPath}
                    onAddMultipleSection={props.onAddMultipleSection}
                    sectionSpec={state.sectionSpec}
                />
            );

        default:
            return null;
    }
};

export default FormSection;
